import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RELEASE_COMPLIANCE_STATUS_OPTIONS } from '@/constants/releases';

/**
 * Filter key mapping constants
 */
export const FILTER_KEY_MAP = {
  ALERT_TYPE: 'alertType',
  APPLICATION: 'application',
  PRIORITY: 'priority',
  RESOURCE_GROUP: 'resourceGroup',
  BUSINESS_FUNCTION: 'businessFunc',
  NAME_SPACE: 'namespace',
  TABLES: 'tables',
  ALL_RESOURCES: 'allResources',
  RELEASE_APPLICATION: 'applicationIds',
  RELEASE_STATUS: 'releaseStatusCodes',
  RELEASE_DATES: 'releaseDatesCodes',
  ARCHIVE_STATUS: 'archiveStatusCodes',
  JIRA_PROJECT: 'jiraProjectIds',
  CREATED_BY: 'createdByIds',
  COMPLIANCE_STATUS: 'complianceStatus',
};

/**
 * Default compliance status options
 */
export const DEFAULT_COMPLIANCE_VALUES = [
  RELEASE_COMPLIANCE_STATUS_OPTIONS.COMPLAINT.label,
  RELEASE_COMPLIANCE_STATUS_OPTIONS.NOT_COMPLAINT.label,
  RELEASE_COMPLIANCE_STATUS_OPTIONS.PENDING.label,
];

const EMPTY_RELEASE_FILTERS = {};

/**
 * Convert filter name to Redux key
 * @param {string} key - Filter name constant
 * @return {string} Redux state key
 */
export function convertFinalKey(key) {
  return FILTER_KEY_MAP[key] || 'unknown';
}

/**
 * Base filter hook - provides common filter logic
 * @param {Object} config - Configuration object
 * @param {string} config.page - Page identifier
 * @param {Object} config.filterData - Filter configuration data
 * @param {Object} config.apiData - API response data
 * @return {Object} Filter state and handlers
 */
export const useBaseFilter = ({ page, filterData, apiData }) => {
  const dispatch = useDispatch();
  const path = useLocation().pathname;

  // Redux selectors
  const filtersObj = useSelector((state) => state.filters.selectedFilters);
  const releaseFiltersObj = useSelector((state) =>
    page === 'RELEASE_LIST_PAGE'
      ? state.releaseList.filters
      : page === 'RELEASE_CALENDAR_PAGE'
        ? state.releaseCalendar.filters
        : EMPTY_RELEASE_FILTERS,
  );
  const resourceFiltersObj = useSelector(
    (state) => state.filters.resourceSelectedFilters,
  );

  // Local state
  const [reff, setReff] = useState(true);
  const [defaultVal, setDefaultVal] = useState([]);
  const [relStatusVal, setRelStatusVal] = useState([]);
  const [relDatesVal, setRelDatesVal] = useState([]);
  const [archiveVal, setArchiveVal] = useState([]);
  const [initialRelStatusVal, setInitialRelStatusVal] = useState([]);
  const [defaultComplianceValue, setDefaultComplianceValue] = useState(
    DEFAULT_COMPLIANCE_VALUES,
  );

  /**
   * Merge filter config with API data
   */
  const mergedData = useMemo(() => {
    if (!apiData || !filterData) return [];

    const otherFilters = filterData.filter((fill) => !fill.default);
    const finalSortedFilters = otherFilters.sort(
      (filter1, filter2) => filter1.sortOrder - filter2.sortOrder,
    );

    return finalSortedFilters.map((fill) => {
      let options = apiData[fill.name];

      // Hardcode compliance status options
      if (fill.name === 'COMPLIANCE_STATUS') {
        options = [
          {
            label: RELEASE_COMPLIANCE_STATUS_OPTIONS.COMPLAINT.label,
            value: RELEASE_COMPLIANCE_STATUS_OPTIONS.COMPLAINT.label,
            isSelected: true,
          },
          {
            label: RELEASE_COMPLIANCE_STATUS_OPTIONS.NOT_COMPLAINT.label,
            value: RELEASE_COMPLIANCE_STATUS_OPTIONS.NOT_COMPLAINT.label,
            isSelected: true,
          },
          {
            label: RELEASE_COMPLIANCE_STATUS_OPTIONS.PENDING.label,
            value: RELEASE_COMPLIANCE_STATUS_OPTIONS.PENDING.label,
            isSelected: true,
          },
        ];
      }

      return {
        ...fill,
        options: options,
      };
    });
  }, [apiData, filterData]);

  /**
   * Get active filter count
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (page === 'ALERT_PAGE') {
      if (filtersObj.alertName) count++;
      if (filtersObj.alertType?.length > 0) count++;
    } else if (page === 'RESOURCE_PAGE') {
      if (resourceFiltersObj.application?.length > 0) count++;
      if (resourceFiltersObj.resourceName?.length > 0) count++;
      if (resourceFiltersObj.namespace?.length > 0) count++;
      if (resourceFiltersObj.resourceGroup?.length > 0) count++;
    } else if (
      page === 'RELEASE_LIST_PAGE' ||
      page === 'RELEASE_CALENDAR_PAGE'
    ) {
      if (releaseFiltersObj.releaseName) count++;
      if (releaseFiltersObj.applicationIds?.length > 0) count++;
      if (releaseFiltersObj.jiraProjectIds?.length > 0) count++;
      if (releaseFiltersObj.createdByIds?.length > 0) count++;
    }
    return count;
  }, [page, filtersObj, releaseFiltersObj, resourceFiltersObj]);

  // Initialize default values from API data (only if not persisted)
  useEffect(() => {
    if (apiData) {
      const relStatus = apiData?.RELEASE_STATUS;
      const relDates = apiData?.RELEASE_DATES;
      const archiveStatus = apiData?.ARCHIVE_STATUS;
      const maindata = apiData?.ALERT_TYPE;

      // Only set from API if Redux state is empty (not persisted)
      if (
        maindata &&
        maindata.length > 0 &&
        (!filtersObj.alertType || filtersObj.alertType.length === 0)
      ) {
        const alertTypeArray = maindata
          .filter((alert) => alert.isSelected)
          .map((alert) => alert.value);
        setDefaultVal(alertTypeArray);
      }

      if (
        relStatus &&
        relStatus.length > 0 &&
        (!releaseFiltersObj.releaseStatusCodes ||
          releaseFiltersObj.releaseStatusCodes.length === 0)
      ) {
        const releaseStatus = relStatus
          .filter((release) => release.isSelected)
          .map((release) => release.value);
        setRelStatusVal(releaseStatus);
        setInitialRelStatusVal(releaseStatus);
      }

      if (
        relDates &&
        relDates.length > 0 &&
        (!releaseFiltersObj.releaseDatesCodes ||
          releaseFiltersObj.releaseDatesCodes.length === 0)
      ) {
        const arr = relDates
          .filter((release) => release.isSelected)
          .map((release) => release.value);
        setRelDatesVal(arr);
      }

      if (
        archiveStatus &&
        archiveStatus.length > 0 &&
        (!releaseFiltersObj.archiveStatusCodes ||
          releaseFiltersObj.archiveStatusCodes.length === 0)
      ) {
        const archiveStatusArray = archiveStatus
          .filter((release) => release.isSelected)
          .map((release) => release.value);
        setArchiveVal(archiveStatusArray);
      }
    }
  }, [apiData, filtersObj.alertType, releaseFiltersObj]);

  // Sync alert type with Redux
  useEffect(() => {
    if (page === 'ALERT_PAGE' && filtersObj?.alertType) {
      setDefaultVal(filtersObj.alertType);
    }
  }, [filtersObj?.alertType, page]);

  // Sync release filters with Redux
  useEffect(() => {
    if (page === 'RELEASE_LIST_PAGE' || page === 'RELEASE_CALENDAR_PAGE') {
      if (releaseFiltersObj?.releaseStatusCodes) {
        setRelStatusVal(releaseFiltersObj.releaseStatusCodes);
      }
      if (releaseFiltersObj?.releaseDatesCodes) {
        setRelDatesVal(releaseFiltersObj.releaseDatesCodes);
      }
      if (releaseFiltersObj?.archiveStatusCodes) {
        setArchiveVal(releaseFiltersObj.archiveStatusCodes);
      }
    }
  }, [releaseFiltersObj, page]);

  // Sync compliance status
  useEffect(() => {
    if (page === 'RELEASE_CALENDAR_PAGE') {
      if (
        releaseFiltersObj?.complianceStatus &&
        releaseFiltersObj.complianceStatus.length > 0
      ) {
        setDefaultComplianceValue(releaseFiltersObj.complianceStatus);
      } else {
        setDefaultComplianceValue(DEFAULT_COMPLIANCE_VALUES);
      }
    }
  }, [releaseFiltersObj?.complianceStatus, page]);

  return {
    dispatch,
    path,
    filtersObj,
    releaseFiltersObj,
    resourceFiltersObj,
    reff,
    setReff,
    defaultVal,
    setDefaultVal,
    relStatusVal,
    setRelStatusVal,
    relDatesVal,
    setRelDatesVal,
    archiveVal,
    setArchiveVal,
    initialRelStatusVal,
    defaultComplianceValue,
    setDefaultComplianceValue,
    mergedData,
    activeFilterCount,
  };
};
